/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { PrimaryButton } from '@fluentui/react/lib/Button';
import { DetailsRow, IColumn, IDetailsRowProps, IDetailsRowStyles } from '@fluentui/react/lib/DetailsList';
import { DialogType, IDialogContentStyleProps, IDialogContentStyles } from '@fluentui/react/lib/Dialog';
import { FontIcon, Icon } from '@fluentui/react/lib/Icon';
import { IScrollablePaneProps, IScrollablePaneStyles, ScrollablePane } from '@fluentui/react/lib/ScrollablePane';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { Stack } from '@fluentui/react/lib/Stack';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { classNamesFunction, IRenderFunction, SelectionMode } from '@fluentui/react/lib/Utilities';
import orderBy from 'lodash.orderby';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { BasicModal, BasicModalProps } from 'src/core/components/BasicModal';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { NameCellLink } from 'src/core/components/NameCellLink';
import { Table } from 'src/core/components/Table';
import { SortKeyType, TableProps } from 'src/core/components/TableProps';
import { TruncatedText } from 'src/core/components/TruncatedText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { withModalMounter } from 'src/core/utils/hocs/withModalMounter';
import { routeTable } from 'src/core/utils/routeTable';
import { getLanguageStudioBaseUrl } from 'src/core/utils/routingUtils';
import { createKeyedStringNormalizer } from 'src/core/utils/strings';
import { CluMigrationInfoDto } from 'src/luis/verticals/cluMigrationModal/CluMigrationModal';
import styled from 'styled-components';

const messages = defineMessages({
    modalTitle: {
        id: 'cluMigration.migratingLuisApplicationsModal.modalTitle',
        defaultMessage: 'Migrating applications'
    },
    description: {
        id: 'cluMigration.migratingLuisApplicationsModal.description',
        defaultMessage: 'Your applications are currently being copied from LUIS to conversational language understanding.'
    },
    luisApplicationName: {
        id: 'cluMigration.migratingLuisApplicationsModal.luisApplicationName',
        defaultMessage: 'LUIS application name'
    },
    status: {
        id: 'cluMigration.migratingLuisApplicationsModal.status',
        defaultMessage: 'Status'
    },
    version: {
        id: 'cluMigration.migratingLuisApplicationsModal.version',
        defaultMessage: 'Version'
    },
    cluProject: {
        id: 'cluMigration.migratingLuisApplicationsModal.cluProject',
        defaultMessage: 'CLU project'
    },
    done: {
        id: 'cluMigration.migratingLuisApplicationsModal.done',
        defaultMessage: 'Done'
    },
    succeeded: {
        id: 'cluMigration.migratingLuisApplicationsModal.succeeded',
        defaultMessage: 'Succeeded'
    },
    failed: {
        id: 'cluMigration.migratingLuisApplicationsModal.failed',
        defaultMessage: 'Failed'
    },
    inProgress: {
        id: 'cluMigration.migratingLuisApplicationsModal.inProgress',
        defaultMessage: 'In progress'
    },
    notStarted: {
        id: 'cluMigration.migratingLuisApplicationsModal.notStarted',
        defaultMessage: 'Not started'
    },
    includes: {
        id: 'cluMigration.migratingLuisApplicationsModal.includes',
        defaultMessage: 'Your application includes'
    },
    patterns: {
        id: 'cluMigration.migratingLuisApplicationsModal.patterns',
        defaultMessage: 'patterns'
    },
    patternAnyEntities: {
        id: 'cluMigration.migratingLuisApplicationsModal.patternAnyEntities',
        defaultMessage: 'patterns.any entities'
    },
    phraseListFeatures: {
        id: 'cluMigration.migratingLuisApplicationsModal.phraseListFeatures',
        defaultMessage: 'phrase list features'
    },
    entities: {
        id: 'cluMigration.migratingLuisApplicationsModal.entities',
        defaultMessage: 'entities as a feature to intents'
    },
    versions: {
        id: 'cluMigration.migratingLuisApplicationsModal.versions',
        defaultMessage: 'versions'
    },
    structuredEntities: {
        id: 'cluMigration.migratingLuisApplicationsModal.structuredEntities',
        defaultMessage: 'structured entities.'
    },
    features: {
        id: 'cluMigration.migratingLuisApplicationsModal.features',
        defaultMessage: 'These features will not be transferred to conversational language understanding.'
    },
    leafNodes: {
        id: 'cluMigration.migratingLuisApplicationsModal.leafNodes',
        defaultMessage: 'The leaf nodes of your structure will be added as flat entities to conversational language understanding.'
    },
    learnMore: {
        id: 'cluMigration.migratingLuisApplicationsModal.learnMore',
        defaultMessage: 'Learn more'
    }
});

export type MigratingApp = {
    id: string;
    luisName: string;
    version: string;
    status: string;
    cluName: string;
    failureReason: string;
};

type MigratingLuisApplicationsModalProps = BasicModalProps<object> &
    TableProps<MigratingApp> & {
        cluMigrationInfoDto: CluMigrationInfoDto;
    };

const localTheme = getScopedTheme('MigratingLuisApplicationsModal');
const borderStyle = `solid 1px ${localTheme.borderColor}`;
const dialogContentStyles = classNamesFunction<IDialogContentStyleProps, IDialogContentStyles>()({
    inner: { padding: '0px', borderTop: borderStyle, overflow: 'hidden' }
});
const ScrollablePaneStyles = classNamesFunction<IScrollablePaneProps, IScrollablePaneStyles>()({
    root: { height: '100%', position: 'relative' }
});
const FooterWrapper = styled(Stack)(() => ({
    borderTop: borderStyle,
    bottom: '0px',
    width: '100%'
}));
const RowContainer = styled(Stack)(() => ({
    borderBottom: `${localTheme.focusingColor} solid 1px`,
    $nest: {
        '&:hover': { backgroundColor: localTheme.hoveringColor }
    }
}));
const detailsRowStyles = classNamesFunction<IDetailsRowProps, IDetailsRowStyles>()({
    root: {
        borderBottom: 'none',
        backgroundColor: 'transparent !important',
        userSelect: 'auto'
    },
    cell: {
        overflow: 'visible'
    },
    check: {
        cursor: 'pointer !important'
    }
});

export const MigratingLuisApplicationsModal = withModalMounter(
    observer((props: MigratingLuisApplicationsModalProps) => {
        const { cluMigrationInfoDto, onSubmit, ...rest } = props;
        const { dispatcher, rootStore } = useGlobalState();
        const { cluMigrationStoreHouse } = rootStore;
        const { cluMigrationJobsStoreHouse } = cluMigrationStoreHouse;
        const { localize } = useLocalization();
        const { isLargeOrGreater } = useResponsiveMode();

        React.useEffect(() => {
            dispatcher.dispatch('migrateLuisApps', {
                luisAppsMetadata: cluMigrationInfoDto.applications.map(app => ({ appId: app.id, appVersion: app.selectedVersion })),
                languageResource: cluMigrationInfoDto.languageResource
            });
        }, []);

        const [expandedAppsIds, setExpandedAppsIds] = React.useState([]);

        const applications = cluMigrationJobsStoreHouse.items[0]?.luisAppsMigrationState.map(app => {
            const tmpApp = cluMigrationInfoDto.applications.find(a => a.id === app.appId);

            return {
                id: tmpApp?.id,
                luisName: tmpApp?.name,
                version: tmpApp?.selectedVersion,
                status: app.status,
                cluName: app.appName,
                failureReason: app.failureReason
            };
        });

        const [sortKey, setSortKey] = React.useState<SortKeyType<MigratingApp>>({ key: 'luisName', order: 'asc' });

        const handleColumnClick = (c: IColumn) => {
            setSortKey({ key: c.key as keyof MigratingApp, order: c.isSortedDescending ? 'asc' : 'desc' });
        };

        const orderedApplications = orderBy(applications, [createKeyedStringNormalizer(sortKey.key)], [sortKey.order]);

        const onChevronClick = (item: MigratingApp) => {
            if (expandedAppsIds.includes(item.id)) {
                const tmpExpandedAppsIds = expandedAppsIds.filter(id => id !== item.id);
                setExpandedAppsIds(tmpExpandedAppsIds);
            } else {
                const tmpExpandedAppsIds = [...expandedAppsIds, item.id];
                setExpandedAppsIds(tmpExpandedAppsIds);
            }
        };

        const onRenderStatus = (status: string) => {
            switch (status) {
                case 'succeeded':
                    return (
                        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }}>
                            <FontIcon iconName="Completed" style={{ color: localTheme.successColor }} />
                            <LuisText>{localize(messages.succeeded)}</LuisText>
                        </Stack>
                    );
                case 'notStarted':
                    return <LuisText>{localize(messages.notStarted)}</LuisText>;
                case 'running':
                    return (
                        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }}>
                            <Spinner size={SpinnerSize.small} />
                            <LuisText>{localize(messages.inProgress)}</LuisText>
                        </Stack>
                    );
                case 'failed':
                default:
                    return (
                        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }}>
                            <FontIcon iconName="Warning" style={{ color: localTheme.failureColor }} />
                            <LuisText>{localize(messages.failed)}</LuisText>
                        </Stack>
                    );
            }
        };

        const getWarningMessage = (appId: string, versionId: string) => {
            if (applications.find(app => app.id === appId).failureReason) {
                return (
                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 20, padding: '10px 0 20px 40px' }}>
                        <Icon iconName="Warning" styles={{ root: { color: localTheme.failureColor, fontSize: '14px' } }} />
                        <Stack horizontalAlign="center">
                            <LuisText variant="small">{applications.find(app => app.id === appId).failureReason}</LuisText>
                        </Stack>
                    </Stack>
                );
            }
            const app = rootStore.luisAppStoreHouse.findById(appId);
            const appVersion = app?.appVersionStoreHouse.findById(versionId);

            if (!appVersion) {
                return null;
            }

            const message: string[] = [];

            if (appVersion.patternStoreHouse.items.length > 0) {
                message.push(localize(messages.patterns));
            }

            if (appVersion.entityStoreHouse.patternAnyEntities.length > 0) {
                message.push(localize(messages.patternAnyEntities));
            }

            if (appVersion.phraseListStoreHouse.items.length > 0) {
                message.push(localize(messages.phraseListFeatures));
            }

            if (appVersion.intentStoreHouse.items.some(item => item.featureStoreHouse.items.some(it => it.typeId === 'model'))) {
                message.push(localize(messages.entities));
            }

            if (app.appVersionStoreHouse.items.length > 1) {
                message.push(localize(messages.versions));
            }

            if (message.length) {
                return (
                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 20, padding: '10px 0 20px 40px' }}>
                        <Icon iconName="Warning12" styles={{ root: { color: localTheme.warningColor, fontSize: '14px' } }} />
                        <Stack tokens={{ childrenGap: 5 }}>
                            {appVersion.entityStoreHouse.simpleEntities.some(item => item.children.length) && (
                                <LuisText>
                                    <LuisText variant="small">{localize(messages.includes)}</LuisText>
                                    &nbsp;
                                    <LuisText fontWeight={600} variant="small">
                                        {localize(messages.structuredEntities)}
                                    </LuisText>
                                    &nbsp;
                                    <LuisText variant="small">{localize(messages.features)}</LuisText>
                                </LuisText>
                            )}
                            <LuisText variant="small">
                                <LuisText variant="small">{localize(messages.includes)}</LuisText>
                                &nbsp;
                                <LuisText fontWeight={600} variant="small">
                                    {/* This regex is used to replace the last comma in the message by and */}
                                    {message.join(', ').replace(/,(?=[^,]*$)/, ' and')}.
                                </LuisText>
                                &nbsp;
                                <LuisText variant="small">{localize(messages.features)}</LuisText>
                                &nbsp;
                                <ExternalLink>{localize(messages.learnMore)}</ExternalLink>
                            </LuisText>
                        </Stack>
                    </Stack>
                );
            }

            return null;
        };

        const onRenderRow: IRenderFunction<IDetailsRowProps> = rowProps => (
            <RowContainer id={`${rowProps.itemIndex}`} tokens={{ childrenGap: 10 }}>
                <DetailsRow {...rowProps} styles={detailsRowStyles} />
                {expandedAppsIds.includes(rowProps.item.id) && <>{getWarningMessage(rowProps.item.id, rowProps.item.version)}</>}
            </RowContainer>
        );

        const columns: IColumn[] = [
            {
                key: 'luisName',
                name: localize(messages.luisApplicationName),
                minWidth: 250,
                maxWidth: 300,
                isRowHeader: true,
                isResizable: true,
                isSorted: sortKey?.key === 'luisName',
                isSortedDescending: sortKey?.key === 'luisName' && sortKey?.order === 'desc',
                onRender: (item: MigratingApp) => (
                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 5 }}>
                        {Boolean(getWarningMessage(item.id, item.version)) && (
                            <FontIcon
                                onClick={() => onChevronClick(item)}
                                iconName={expandedAppsIds.includes(item.id) ? 'ChevronDown' : 'ChevronRight'}
                                style={{ cursor: 'pointer' }}
                            />
                        )}
                        <TruncatedText block nowrap maxWidth={300}>
                            {item.luisName}
                        </TruncatedText>
                    </Stack>
                )
            },
            {
                key: 'status',
                name: localize(messages.status),
                minWidth: 150,
                maxWidth: 200,
                isResizable: true,
                isSorted: sortKey?.key === 'status',
                isSortedDescending: sortKey?.key === 'status' && sortKey?.order === 'desc',
                onRender: (item: MigratingApp) => <LuisText>{onRenderStatus(item.status)}</LuisText>
            },
            {
                key: 'version',
                name: localize(messages.version),
                minWidth: 150,
                maxWidth: 200,
                isResizable: true,
                isSorted: sortKey?.key === 'version',
                isSortedDescending: sortKey?.key === 'version' && sortKey?.order === 'desc',
                onRender: (item: MigratingApp) => (
                    <TruncatedText block nowrap>
                        {item.version}
                    </TruncatedText>
                )
            },
            {
                key: 'cluName',
                name: localize(messages.cluProject),
                minWidth: 150,
                maxWidth: 200,
                isResizable: true,
                isSorted: sortKey?.key === 'cluName',
                isSortedDescending: sortKey?.key === 'cluName' && sortKey?.order === 'desc',
                onRender: (item: MigratingApp) =>
                    item.status === 'succeeded' ? (
                        <TruncatedText block nowrap>
                            <NameCellLink onClick={() => onCluNameClick(item.cluName)}>{item.cluName}</NameCellLink>
                        </TruncatedText>
                    ) : (
                        <TruncatedText block nowrap>
                            {item.cluName}
                        </TruncatedText>
                    )
            }
        ];

        const isMigrationInProgress =
            cluMigrationJobsStoreHouse.tracker.isRunning ||
            cluMigrationJobsStoreHouse.items.some(job => job.status !== 'succeeded' && job.status !== 'failed');

        const onRenderFooter = () => (
            <FooterWrapper horizontalAlign="end" tokens={{ padding: '16px 24px' }}>
                <PrimaryButton disabled={isMigrationInProgress} onClick={onSubmit}>
                    {localize(messages.done)}
                </PrimaryButton>
            </FooterWrapper>
        );

        const onCluNameClick = (projectName: string) => {
            let url = getLanguageStudioBaseUrl().concat(routeTable.clu.projectPage(projectName));

            if (rootStore.azureStore.session.csAccountStore.id !== cluMigrationInfoDto.languageResource.id) {
                url = url.concat(`?csAccountPath=${cluMigrationInfoDto.languageResource.path}`);
            }

            window.open(url);
        };

        return (
            <BasicModal
                {...props}
                minWidth={976}
                onRenderFooter={onRenderFooter}
                dialogContentProps={{ title: localize(messages.modalTitle), styles: dialogContentStyles, type: DialogType.normal }}>
                <Stack tokens={{ childrenGap: 20, padding: '0px 24px' }}>
                    <LuisText styles={{ root: { paddingTop: 20 } }}>{localize(messages.description)}</LuisText>
                    <ScrollablePane
                        styles={mergeStyleSets(ScrollablePaneStyles, { root: { minHeight: isLargeOrGreater ? '480px' : '350px' } })}>
                        <Table<MigratingApp>
                            columns={columns}
                            onRenderRow={onRenderRow}
                            items={orderedApplications}
                            selectionPreservedOnEmptyClick
                            onSortColumn={handleColumnClick}
                            selectionMode={SelectionMode.none}
                            isLoading={cluMigrationJobsStoreHouse.tracker.isRunning || !cluMigrationJobsStoreHouse.items?.[0]}
                            {...rest}
                        />
                    </ScrollablePane>
                </Stack>
            </BasicModal>
        );
    })
);
